import React from "react";
import {
	Box,
	Container,
	Row,
	Column,
	Heading,
	FooterLink,
	BrandSection,
	ContactItem,
	SocialRow,
	SocialLink,
	LegalBar,
	LegalInner,
	LegalLinks,
	DevelopmentLink,
} from "./FooterStyles";

import LogoImg from "../Logo/lgfinal.png";

import FacebookIcon from '@material-ui/icons/Facebook';
import TwitterIcon from '@material-ui/icons/Twitter';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import InstagramIcon from '@material-ui/icons/Instagram';
import RoomIcon from '@material-ui/icons/Room';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useTranslation } from 'react-i18next';
import useSubdomainUrl from '../../subdomains/useSubdomainUrl';
import SubscriptionForm from './SubscriptionForm';

const aboutLinks = [
	{ label: 'About the Ministry', href: '/profile' },
	{ label: 'Directorates and offices', href: '/directorates' },
	{ label: 'Development partners', href: '/partners' },
	{ label: 'Former Ministers', href: '/formerministers' },
	{ label: 'Careers and vacancies', href: '/vacancies' },
	{ label: 'News and announcements', href: '/latestnews' },
];

const serviceLinks = [
	{ label: 'Budget and spending', href: '/budgetspendingpage' },
	{ label: 'Current tenders', href: '/tenders' },
	{ label: 'Publications and reports', href: '/publications' },
	{ label: 'Treasury documents', href: '/alltreasurydocuments' },
	{ label: 'Find open quotes', href: 'http://www.eprocurement.gov.tl/', external: true },
	{ label: 'Track your goods', href: 'https://customs.gov.tl/', external: true },
	{ label: 'Open data and statistics', href: 'https://inetl-ip.gov.tl', external: true },
	{ label: 'Staff webmail', href: 'https://webmail.mof.gov.tl/', external: true },
	// lia.mof.gov.tl when reachable, otherwise www.mof.gov.tl/lia (set in Footer below).
	{ label: 'LIA AI assistant', subdomain: 'lia' },
];

const Footer = () => {
	const { t } = useTranslation();
	const siteUrl = useSubdomainUrl();

	return (
		<Box>
			<Container>
				<SubscriptionForm />
				<Row>
					<Column>
						<BrandSection>
							<img src={LogoImg} alt="Ministry of Finance, Timor-Leste" />
							<p>
								The Ministry of Finance is responsible for the design, execution and
								monitoring of the financial and economic policies of the Democratic
								Republic of Timor-Leste.
							</p>
						</BrandSection>
						<SocialRow>
							<SocialLink
								href="https://www.facebook.com/MinisteriodasFinancasTL"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Ministry of Finance on Facebook"
							>
								<FacebookIcon />
							</SocialLink>
							<SocialLink
								href="https://twitter.com/"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Ministry of Finance on Twitter"
							>
								<TwitterIcon />
							</SocialLink>
							<SocialLink
								href="https://www.linkedin.com/"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Ministry of Finance on LinkedIn"
							>
								<LinkedInIcon />
							</SocialLink>
							<SocialLink
								href="https://www.instagram.com/"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Ministry of Finance on Instagram"
							>
								<InstagramIcon />
							</SocialLink>
						</SocialRow>
					</Column>

					<Column>
						<Heading>{t('About')}</Heading>
						{aboutLinks.map((link) => (
							<FooterLink key={link.label} href={link.href}>
								{t(link.label)}
							</FooterLink>
						))}
					</Column>

					<Column>
						<Heading>{t('Services and data')}</Heading>
						{serviceLinks.map((link) => (
							<FooterLink
								key={link.label}
								href={link.subdomain ? siteUrl(link.subdomain) : link.href}
								target={link.external ? '_blank' : undefined}
								rel={link.external ? 'noopener noreferrer' : undefined}
							>
								{t(link.label)}
								{link.external && <OpenInNewIcon style={{ fontSize: 13 }} />}
							</FooterLink>
						))}
					</Column>

					<Column>
						<Heading>{t('Contact us')}</Heading>
						<FooterLink href="/contact">{t('Contact the Ministry')}</FooterLink>
						<ContactItem>
							<RoomIcon />
							<span>
								<a
									href="https://www.google.com/maps/search/?api=1&query=Ministry+of+Finance+Building+Torre+Minist%C3%A9rio+das+Finan%C3%A7as+Aitarak-Laran+Dili+Timor-Leste"
									target="_blank"
									rel="noopener noreferrer"
								>
									Ministry of Finance Building (Torre Ministério das Finanças)<br />
									Aitarak-Laran, Dili, Timor-Leste
								</a>
								<br />
								<a
									href="https://www.google.com/maps/search/?api=1&query=Ministry+of+Finance+Building+Torre+Minist%C3%A9rio+das+Finan%C3%A7as+Aitarak-Laran+Dili+Timor-Leste"
									target="_blank"
									rel="noopener noreferrer"
								>
									View map
								</a>
							</span>
						</ContactItem>
						<ContactItem>
							<MailOutlineIcon />
							<a href="mailto:info@mof.gov.tl">info@mof.gov.tl</a>
						</ContactItem>
						<ContactItem>
							<ScheduleIcon />
							<span>Monday to Friday, 08:00 – 17:00</span>
						</ContactItem>
					</Column>
				</Row>
			</Container>

			<LegalBar>
				<Container>
					<LegalInner>
						<DevelopmentLink>
							Copyright &copy; {new Date().getFullYear()} Ministry of Finance, RDTL.
							All rights reserved.<br />
							Developed and maintained by the{' '}
							<span>Information Technology Unit (IFMISU)</span>
						</DevelopmentLink>
						<LegalLinks aria-label="Legal and site information">
							<a href="/contact">{t('Contact')}</a>
							<a href="/searchpage">{t('Search')}</a>
							<a href="/alltreasurydocuments">{t('Laws and regulations')}</a>
							<a href="/profile">{t('Accessibility')}</a>
						</LegalLinks>
					</LegalInner>
				</Container>
			</LegalBar>
		</Box>
	);
};

export default Footer;
